import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {Array<{id?: number|string, sku?: string|null, options?: string[], option1?: string, option2?: string, option3?: string}> | undefined} */
  #skuVariants;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
    this.updateRequiredDropdownPlaceholderClasses();
    const autoSelectedSelect = this.updateSkuOptionAvailability();

    // When required dropdowns + SKU filtering leave only one valid choice,
    // updateSkuOptionAvailability auto-selects it on initial load. That does not
    // fire a native change event, so dependent server-rendered blocks (price,
    // bulk tiers, buy buttons) can otherwise stay stuck on Shopify's initial
    // selected_or_first_available_variant. Defer the synthetic change until the
    // rest of the product section has mounted, otherwise later blocks can miss
    // the variant:update event.
    if (autoSelectedSelect && !this.hasRequiredDropdownPlaceholderSelection) {
      const matchedSkuVariantId = this.getSelectedSkuVariant()?.id?.toString();
      const selectedOptionVariantId = autoSelectedSelect.selectedOptions[0]?.dataset.variantId;
      this.updateProductPageVariantUrl(matchedSkuVariantId || selectedOptionVariantId);

      requestAnimationFrame(() => {
        autoSelectedSelect.dispatchEvent(new Event('change', { bubbles: true }));
      });
      return;
    }

    this.updateRequiredDropdownState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    const autoSelectedSelect = this.updateSkuOptionAvailability();

    if (this.hasRequiredDropdownPlaceholderSelection) {
      this.updateRequiredDropdownState();
      return;
    }

    const requestOption = autoSelectedSelect?.selectedOptions[0] || selectedOption;

    this.dispatchEvent(new VariantSelectedEvent({
      id: requestOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.isOnCurrentProductPage &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = requestOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(requestOption), morphElementSelector);

    const url = new URL(window.location.href);

    const matchedSkuVariantId = this.getSelectedSkuVariant()?.id?.toString();
    const variantId = matchedSkuVariantId || requestOption.dataset.variantId || null;

    if (isOnProductPage) {
      this.updateProductPageVariantUrl(variantId, url);
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
      this.updateRequiredDropdownPlaceholderClasses();
    }
  }

  updateRequiredDropdownPlaceholderClasses() {
    if (this.dataset.requireDropdownSelections !== 'true') return;

    for (const select of this.querySelectorAll('select.variant-option__select')) {
      if (select instanceof HTMLSelectElement) {
        select.classList.toggle('is-placeholder', select.value === '');
      }
    }
  }

  get skuVariants() {
    if (this.#skuVariants) return this.#skuVariants;

    const variantsJson = this.querySelector('script[data-sku-variants]')?.textContent;
    if (!variantsJson) {
      this.#skuVariants = [];
      return this.#skuVariants;
    }

    try {
      const variants = JSON.parse(variantsJson);
      this.#skuVariants = Array.isArray(variants)
        ? variants.filter((variant) => typeof variant?.sku === 'string' && variant.sku.trim() !== '')
        : [];
    } catch (_error) {
      this.#skuVariants = [];
    }

    return this.#skuVariants;
  }

  updateSkuOptionAvailability() {
    if (this.dataset.hideOptionsWithoutSku !== 'true') return undefined;

    const selects = Array.from(this.querySelectorAll('select.variant-option__select')).filter(
      (select) => select instanceof HTMLSelectElement
    );
    const variants = this.skuVariants;

    if (!selects.length || !variants.length) return undefined;

    let changedSelection = false;
    /** @type {HTMLSelectElement | undefined} */
    let autoSelectedSelect;

    // Re-run availability after auto-selecting a sole valid option, because one
    // selection can reduce later dropdowns to a single SKU-backed choice too.
    for (let pass = 0; pass < selects.length; pass++) {
      let selectedSoleOptionThisPass = false;

      for (const [selectIndex, select] of selects.entries()) {
        for (const option of select.options) {
          if (option.value === '') continue;

          const isHidden = !this.hasMatchingSkuVariant(selects, selectIndex, option.value);
          option.hidden = isHidden;
          option.disabled = isHidden;
        }

        const visibleOptions = Array.from(select.options).filter((option) => option.value !== '' && !option.hidden);

        if (select.value && select.selectedOptions[0]?.hidden) {
          const placeholder = Array.from(select.options).find((option) => option.value === '');
          select.value = placeholder?.value ?? visibleOptions[0]?.value ?? '';
          changedSelection = true;
          autoSelectedSelect = select;
        }

        if (this.dataset.requireDropdownSelections === 'true' && !select.value && visibleOptions.length === 1) {
          const soleVisibleOption = visibleOptions[0];
          if (!soleVisibleOption) continue;

          select.value = soleVisibleOption.value;
          changedSelection = true;
          selectedSoleOptionThisPass = true;
          autoSelectedSelect = select;
        }
      }

      if (!selectedSoleOptionThisPass) break;
    }

    if (changedSelection) {
      for (const select of selects) {
        for (const option of select.options) {
          option.toggleAttribute('selected', option.value === select.value);
        }
      }
    }

    this.updateRequiredDropdownPlaceholderClasses();

    if (changedSelection) {
      this.dispatchEvent(new CustomEvent('variant-picker:auto-select', {
        bubbles: true,
        detail: {
          productId: this.dataset.productId ?? '',
          sectionId: this.dataset.sectionId ?? '',
          complete: !this.hasRequiredDropdownPlaceholderSelection,
        },
      }));
    }

    return autoSelectedSelect;
  }

  /**
   * @param {HTMLSelectElement[]} selects
   * @param {number} selectIndex
   * @param {string} optionValue
   * @returns {boolean}
   */
  hasMatchingSkuVariant(selects, selectIndex, optionValue) {
    return this.skuVariants.some((variant) => {
      return selects.every((select, index) => {
        const value = index === selectIndex ? optionValue : select.value;
        const variantOption = this.getVariantOptionValue(variant, index);
        return value === '' || variantOption === value;
      });
    });
  }

  /**
   * @param {{options?: string[], option1?: string, option2?: string, option3?: string}} variant
   * @param {number} index
   * @returns {string | undefined}
   */
  getVariantOptionValue(variant, index) {
    if (Array.isArray(variant.options)) return variant.options[index];
    if (index === 0) return variant.option1;
    if (index === 1) return variant.option2;
    if (index === 2) return variant.option3;
    return undefined;
  }

  /**
   * Finds the SKU-backed variant that matches the current complete selection.
   * This is needed for required-dropdown products where SKU filtering auto-selects
   * the only valid choices; individual option elements do not always expose the
   * final variant id, but the embedded product variants JSON does.
   * @returns {{id?: number|string, sku?: string|null, options?: string[], option1?: string, option2?: string, option3?: string} | undefined}
   */
  getSelectedSkuVariant() {
    const selects = Array.from(this.querySelectorAll('select.variant-option__select')).filter(
      (select) => select instanceof HTMLSelectElement
    );

    if (!selects.length) return undefined;
    if (selects.some((select) => select.value === '')) return undefined;

    return this.skuVariants.find((variant) => {
      return selects.every((select, index) => this.getVariantOptionValue(variant, index) === select.value);
    });
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // The option_values param updates Shopify's variant-picker metadata, but
    // some server-rendered blocks still key off selected_variant/variant. Pass
    // the explicit variant id too so blocks like custom bulk tiers render for
    // the same SKU-backed variant the picker has selected. On required-dropdown
    // products with SKU filtering, an individual option may not know the final
    // variant id, so fall back to the SKU-backed variant matching all selects.
    const selectedVariantId = this.getSelectedSkuVariant()?.id || selectedOption.dataset.variantId;
    if (selectedVariantId) {
      params.push(`variant=${selectedVariantId}`);
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          if (this.shouldPreserveRequiredDropdownSelections) {
            newProduct = this.updateVariantPickerMetadata(html);
          } else {
            newProduct = this.updateVariantPicker(html);
          }

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        const selectedVariant = JSON.parse(textContent);

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(selectedVariant, this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }

        this.updateRequiredDropdownState();
        this.updateRequiredDropdownVariantUrl(selectedVariant);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  /**
   * Updates only variant picker metadata without morphing the visible controls.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPickerMetadata(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  get shouldPreserveRequiredDropdownSelections() {
    return this.dataset.requireDropdownSelections === 'true' && !this.hasRequiredDropdownPlaceholderSelection;
  }

  get hasRequiredDropdownPlaceholderSelection() {
    if (this.dataset.requireDropdownSelections !== 'true') return false;

    const selects = Array.from(this.querySelectorAll('select.variant-option__select')).filter(
      (select) => select instanceof HTMLSelectElement
    );

    return selects.some((select) => select.value === '');
  }

  /**
   * Disables product purchase controls while required variant dropdowns still have placeholder values.
   */
  updateRequiredDropdownState() {
    if (this.dataset.requireDropdownSelections !== 'true') return;

    const hasPlaceholderSelection = this.hasRequiredDropdownPlaceholderSelection;
    const productForm = document.querySelector(
      `product-form-component[data-section-id="${this.dataset.sectionId}"][data-product-id="${this.dataset.productId}"]`
    );

    if (!productForm) return;

    const variantInput = productForm.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement && hasPlaceholderSelection) {
      variantInput.value = '';
    }

    const addToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = hasPlaceholderSelection;
    }

    const acceleratedCheckout = productForm.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (acceleratedCheckout instanceof HTMLElement) {
      acceleratedCheckout.toggleAttribute('hidden', hasPlaceholderSelection);
    }
  }

  get isOnCurrentProductPage() {
    if (this.dataset.templateProductMatch === 'true') return true;

    const productPath = this.dataset.productUrl?.split('?')[0];
    if (!productPath) return false;

    const productUrl = new URL(productPath, window.location.origin);
    return window.location.pathname === productUrl.pathname;
  }

  /**
   * Updates the browser URL with the selected variant on the current product page.
   * @param {string | number | null | undefined} variantId
   * @param {URL} [url]
   */
  updateProductPageVariantUrl(variantId, url) {
    if (!this.isOnCurrentProductPage) return;

    const targetUrl = url || new URL(window.location.href);

    if (variantId) {
      targetUrl.searchParams.set('variant', variantId.toString());
    } else {
      targetUrl.searchParams.delete('variant');
    }

    if (!url && targetUrl.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', targetUrl.toString());
      });
    }
  }

  /**
   * Updates the product page URL with the selected variant returned by Shopify.
   * @param {{ id?: string | number } | null} selectedVariant
   */
  updateRequiredDropdownVariantUrl(selectedVariant) {
    if (!this.shouldPreserveRequiredDropdownSelections) return;
    if (!selectedVariant?.id) return;

    this.updateProductPageVariantUrl(selectedVariant.id);
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedSelectOption = Array.from(this.querySelectorAll('select.variant-option__select'))
      .filter((select) => select instanceof HTMLSelectElement)
      .map((select) => select.selectedOptions[0])
      .find((option) => option instanceof HTMLOptionElement && option.dataset.optionValueId);

    const selectedOption = selectedSelectOption || this.querySelector('fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type {(HTMLInputElement | HTMLOptionElement)[]} */
    const selectedOptions = [];

    for (const select of this.querySelectorAll('select.variant-option__select')) {
      if (!(select instanceof HTMLSelectElement)) continue;

      const option = select.selectedOptions[0];
      if (option instanceof HTMLOptionElement && option.dataset.optionValueId) {
        selectedOptions.push(option);
      }
    }

    for (const input of this.querySelectorAll('fieldset input:checked')) {
      if (input instanceof HTMLInputElement && input.dataset.optionValueId) {
        selectedOptions.push(input);
      }
    }

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
